import {
  allPriceChoicesMin,
  allPriceChoicesMax,
  bedItems,
  bathItems,
  allMinSquareFeet,
  allMaxSquareFeet,
  allSections,
  allSortMethodIds,
  DEFAULT_MIN_PRICE,
  DEFAULT_MAX_PRICE,
  DEFAULT_BEDS,
  DEFAULT_BATHS,
  DEFAULT_MIN_SQ_FT,
  DEFAULT_MAX_SQ_FT,
  DEFAULT_SECTION,
  DEFAULT_SORT_METHOD_ID,
  Section,
  allAvailableFeatures,
  MIN_PAGE_NUMBER,
  MAX_PAGE_NUMBER,
} from '@utils/homeSearchConstants';
import { usePathname, useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import { useCallback, useEffect } from 'react';
import {
  FilterProps,
  LocationProps,
  FilterParams,
} from 'types/FindAHome/HomeSearchProps';
import {
  getSessionFilters,
  setSessionFilters,
} from '@services/FindAHome/FiltersSessionStorage';

type Props = LocationProps;

export function useHomeSearchParams({
  city,
  state,
  postalCode,
  distance,
  isLocationUpdating,
}: Props): FilterProps & {
  searchParamsSynced: boolean;
  updateFilterParams: (filterParams: FilterParams) => void;
  updatePageNumberParam: (page: number) => void;
} {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();

  const cityStateZip = pathname
    .replaceAll('/', '')
    .replace('find-a-home', '')
    .replace('move-in-ready', '');
  const cityStateZipParts = cityStateZip.split('--');
  const cityParam = decodeURIComponent(cityStateZipParts[0]);
  const stateParam = decodeURIComponent(cityStateZipParts[1]);
  const postalCodeParam = cityStateZipParts[2];
  const distanceParam = Number(searchParams.get('distance'));

  // Params
  const isMoveInReadyParam = pathname.includes('move-in-ready');
  const minPriceParam = Number(searchParams.get('minPrice'));
  const maxPriceParam = Number(searchParams.get('maxPrice'));
  const bedsParam = Number(searchParams.get('beds'));
  const bathsParam = Number(searchParams.get('baths'));
  const minSquareFeetParam = Number(searchParams.get('minSquareFeet'));
  const maxSquareFeetParam = Number(searchParams.get('maxSquareFeet'));
  const sectionParam = searchParams.get('section');
  const featureSearchParams = decodeURIComponent(
    searchParams.get('features') ?? ''
  );
  const sortIdParam = Number(searchParams.get('sortId'));
  const pageParam = Number(searchParams.get('page'));

  // Default Values
  let isMoveInReady = isMoveInReadyParam;
  let minPrice = minPriceParam;
  let maxPrice = maxPriceParam;
  let beds = bedsParam;
  let baths = bathsParam;
  let minSquareFeet = minSquareFeetParam;
  let maxSquareFeet = maxSquareFeetParam;
  let section = sectionParam as Section;
  let features = featureSearchParams;
  let sortId = sortIdParam;
  let page = pageParam;

  // Session Storage
  const sessionFilters = getSessionFilters();

  // Only apply session storage when user navigates to `/find-a-home` with no params set.
  const applySession = searchParams.size === 0;

  if (applySession) {
    if (sessionFilters.isMoveInReady != null) {
      isMoveInReady = sessionFilters.isMoveInReady;
    }
    if (sessionFilters.minPrice != null) {
      minPrice = sessionFilters.minPrice;
    }
    if (sessionFilters.maxPrice != null) {
      maxPrice = sessionFilters.maxPrice;
    }
    if (sessionFilters.beds != null) {
      beds = sessionFilters.beds;
    }
    if (sessionFilters.baths != null) {
      baths = sessionFilters.baths;
    }
    if (sessionFilters.minSquareFeet != null) {
      minSquareFeet = sessionFilters.minSquareFeet;
    }
    if (sessionFilters.maxSquareFeet != null) {
      maxSquareFeet = sessionFilters.maxSquareFeet;
    }
    if (sessionFilters.section != null) {
      section = sessionFilters.section;
    }
    if (sessionFilters.features != null) {
      features = sessionFilters.features;
    }
    if (sessionFilters.sortId != null) {
      sortId = sessionFilters.sortId;
    }
    if (sessionFilters.page != null) {
      page = sessionFilters.page;
    }
  }

  // Validation
  if (!allPriceChoicesMin.some(({ value }) => minPrice === value)) {
    minPrice = DEFAULT_MIN_PRICE;
  }
  if (!allPriceChoicesMax.some(({ value }) => maxPrice === value)) {
    maxPrice = DEFAULT_MAX_PRICE;
  }
  if (!bedItems.some(({ value }) => beds === value)) {
    beds = DEFAULT_BEDS;
  }
  if (!bathItems.some(({ value }) => baths === value)) {
    baths = DEFAULT_BATHS;
  }
  if (!allMinSquareFeet.some(({ value }) => minSquareFeet === value)) {
    minSquareFeet = DEFAULT_MIN_SQ_FT;
  }
  if (!allMaxSquareFeet.some(({ value }) => maxSquareFeet === value)) {
    maxSquareFeet = DEFAULT_MAX_SQ_FT;
  }
  if (!allSections.some((s) => section === s)) {
    section = DEFAULT_SECTION;
  }
  const decodedFeatures = (decodeURIComponent(features) ?? '').split(',');
  if (
    !decodedFeatures.every((f) =>
      allAvailableFeatures.some((feature) => feature === f)
    )
  ) {
    features = decodedFeatures
      .filter((f) => allAvailableFeatures.some((feature) => feature === f))
      .join(',');
  }
  if (!allSortMethodIds.includes(sortId)) {
    sortId = DEFAULT_SORT_METHOD_ID;
  }
  if (isNaN(page) || page < MIN_PAGE_NUMBER || page > MAX_PAGE_NUMBER) {
    page = MIN_PAGE_NUMBER;
  }

  // Is value synced to URL search params state?
  const citySynced = cityParam === city;
  const stateSynced = stateParam === state;
  const postalCodeSynced = postalCodeParam === postalCode;
  const distanceSynced = distanceParam === distance;
  const minPriceSynced = minPriceParam === minPrice;
  const maxPriceSynced = maxPriceParam === maxPrice;
  const bedsSynced = bedsParam === beds;
  const bathsSynced = bathsParam === baths;
  const minSquareFeetSynced = minSquareFeetParam === minSquareFeet;
  const maxSquareFeetSynced = maxSquareFeetParam === maxSquareFeet;
  const sectionSynced = sectionParam === section;
  const sortIdSynced = sortIdParam === sortId;
  const featuresSynced = features === featureSearchParams;
  const pageSynced = pageParam === page;
  const searchParamsSynced =
    citySynced &&
    stateSynced &&
    postalCodeSynced &&
    distanceSynced &&
    minPriceSynced &&
    maxPriceSynced &&
    bedsSynced &&
    bathsSynced &&
    minSquareFeetSynced &&
    maxSquareFeetSynced &&
    sectionSynced &&
    featuresSynced &&
    sortIdSynced &&
    pageSynced;

  const location = `${city}--${state}--${postalCode}`;
  const allModelsBaseUrl = `/find-a-home/${location}`;
  const moveInReadyBaseUrl = `/find-a-home/move-in-ready/${location}`;
  const baseUrl = isMoveInReady ? moveInReadyBaseUrl : allModelsBaseUrl;

  // Syncs filter state to URL search params and session storage.
  useEffect(() => {
    const noLocation = !city || !state || !postalCode || !distance;
    if (searchParamsSynced || noLocation || isLocationUpdating) {
      return;
    }

    const params = new URLSearchParams();
    params.set('beds', `${beds}`);
    params.set('baths', `${baths}`);
    params.set('distance', `${distance}`);
    params.set('minPrice', `${minPrice}`);
    params.set('maxPrice', `${maxPrice}`);
    params.set('minSquareFeet', `${minSquareFeet}`);
    params.set('maxSquareFeet', `${maxSquareFeet}`);
    params.set('section', `${section}`);
    params.set('sortId', `${sortId}`);
    params.set('features', features ?? '');
    params.set('page', `${page}`);

    setSessionFilters({
      beds,
      baths,
      minPrice,
      maxPrice,
      minSquareFeet,
      maxSquareFeet,
      section,
      sortId,
      isMoveInReady,
      features,
      page,
    });

    const url = `${baseUrl}/?${params.toString()}`;
    router.replace(url, undefined, { shallow: true });
  }, [
    isLocationUpdating,
    router,
    searchParamsSynced,
    city,
    state,
    postalCode,
    distance,
    baseUrl,
    isMoveInReady,
    minPrice,
    maxPrice,
    beds,
    baths,
    minSquareFeet,
    maxSquareFeet,
    section,
    sortId,
    features,
    page,
  ]);

  const updateFilterParams = useCallback(
    (filterParams: FilterParams): void => {
      filterParams = {
        ...filterParams,
        page: MIN_PAGE_NUMBER, // NOTE: Whenever filters change, set the page number back to min page number
      };
      const params = new URLSearchParams(searchParams.toString());
      for (const key in filterParams) {
        params.set(key, `${filterParams[key as keyof FilterParams]}`);
      }

      setSessionFilters({
        ...getSessionFilters(),
        ...filterParams,
      });

      let url = baseUrl;
      if (Object.hasOwn(filterParams, 'isMoveInReady')) {
        url = filterParams.isMoveInReady
          ? moveInReadyBaseUrl
          : allModelsBaseUrl;
      }
      url = `${url}/?${params.toString()}`;

      // NOTE: Replaces history so when user hits back, the user keeps their filters
      router.replace(url, undefined, { shallow: true });
    },
    [router, searchParams, baseUrl, allModelsBaseUrl, moveInReadyBaseUrl]
  );

  const updatePageNumberParam = useCallback(
    (newPage: number) => {
      const params = new URLSearchParams(searchParams.toString());
      params.set('page', `${newPage}`);

      setSessionFilters({
        ...getSessionFilters(),
        page: newPage,
      });

      const url = `${baseUrl}/?${params.toString()}`;
      // NOTE: Maintains history so when user hits back, the user goes to previous page
      router.push(url, undefined, { shallow: true });
    },
    [router, searchParams, baseUrl]
  );

  return {
    beds,
    baths,
    minPrice,
    maxPrice,
    minSquareFeet,
    maxSquareFeet,
    section,
    isMoveInReady,
    features,
    sortId,
    page,
    searchParamsSynced,
    updateFilterParams,
    updatePageNumberParam,
  };
}
